import DOMPurify from 'dompurify'

const allowedRichTextTags = [
  'a',
  'strong',
  'em',
  'p',
  'span',
  'ul',
  'ol',
  'li',
  'b',
  'i',
  's',
  'br',
  'iframe',
  'video',
  'source',
]

const allowedRichTextAttrs = [
  'href',
  'target',
  'src',
  'width',
  'height',
  'controls',
  'allowfullscreen',
  'frameborder',
  'poster',
  'type',
  'allow',
]

export const sanitizeRichText = (text: string) => {
  if (!text || typeof text !== 'string') {
    return ''
  }

  return DOMPurify.sanitize(text, {
    // Whitelist tags (matches HugeRTE valid_elements)
    ALLOWED_TAGS: allowedRichTextTags,

    // Whitelist attributes (includes link, video, and iframe attributes)
    ALLOWED_ATTR: allowedRichTextAttrs,

    // Block all data attributes (Office/Notion metadata)
    ALLOW_DATA_ATTR: false,

    // Keep text content even if tags are removed
    KEEP_CONTENT: true,

    // Return sanitized HTML string
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  })
}

const blockSeparatedSelector = 'p, div, li, br'

/**
 * Plain text for contexts that cannot render HTML, such as a native `title`
 * tooltip. Unlike `sanitizeText`, entities are decoded and line breaks between
 * paragraphs, list items and `<br>`s are kept.
 */
export const richTextToPlainText = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return ''
  }

  // DOMParser documents are inert: scripts don't run and resources don't load.
  const document = new DOMParser().parseFromString(html, 'text/html')
  for (const element of document.body.querySelectorAll(
    blockSeparatedSelector
  )) {
    element.prepend('\n')
    element.append('\n')
  }

  return (document.body.textContent ?? '')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
}

export const sanitizeText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return ''
  }

  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [], // No HTML allowed at all
    KEEP_CONTENT: true,
  })
}
